import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {useCallback, useEffect, useRef, useState} from 'react';
import {
    CAN_REDO_COMMAND,
    CAN_UNDO_COMMAND,
    REDO_COMMAND,
    UNDO_COMMAND,
    SELECTION_CHANGE_COMMAND,
    FORMAT_TEXT_COMMAND,
    FORMAT_ELEMENT_COMMAND,
    $getSelection,
    $isRangeSelection,
    $createParagraphNode,
    LexicalEditor,
} from 'lexical';
import {INSERT_TABLE_COMMAND} from '@lexical/table';
import {$isLinkNode, TOGGLE_LINK_COMMAND} from '@lexical/link';
import {
    $isParentElementRTL,
    $wrapNodes,
    $isAtNodeEnd
} from '@lexical/selection';
import {$getNearestNodeOfType, mergeRegister} from '@lexical/utils';
import {
    INSERT_ORDERED_LIST_COMMAND,
    INSERT_UNORDERED_LIST_COMMAND,
    REMOVE_LIST_COMMAND,
    $isListNode,
    ListNode
} from '@lexical/list';
import {createPortal} from 'react-dom';
import {
    $createHeadingNode,
    $createQuoteNode,
    $isHeadingNode
} from '@lexical/rich-text';
import Modal from '../../modal';
import Button from '../../button';

const LowPriority = 1;

/**
 * Validates URL protocol to prevent XSS attacks
 * Only allows http, https, and mailto protocols
 * Returns empty string for invalid/dangerous URLs
 * Use with encodeURI() for Veracode CWE-80 compliance
 */
function validateUrlProtocol(url: string): string {
    if (!url) {
        return '';
    }

    // Trim whitespace and remove any control characters
    // eslint-disable-next-line no-control-regex
    url = url.trim().replace(/[\u0000-\u001F\u007F]/g, '');

    // Check for dangerous protocols
    const dangerousProtocols = /^(javascript|data|vbscript|file|about|blob|filesystem):/i;
    if (dangerousProtocols.test(url)) {
        console.warn('Blocked potentially dangerous URL protocol');
        return '';
    }

    // Block URLs with encoded dangerous protocols
    try {
        const decodedUrl = decodeURIComponent(url).toLowerCase();
        if (decodedUrl.includes('javascript:') || decodedUrl.includes('data:') || 
            decodedUrl.includes('vbscript:') || decodedUrl.includes('file:')) {
            console.warn('Blocked URL with encoded dangerous protocol');
            return '';
        }
    } catch (e) {
        // Invalid URI encoding
        console.warn('Blocked URL with invalid encoding');
        return '';
    }

    // If no protocol specified, ensure it starts with https://
    if (!/^[a-z][a-z0-9+.-]*:/i.test(url)) {
        url = 'https://' + url;
    }

    // Only allow http, https, and mailto protocols
    if (!/^(https?|mailto):/i.test(url)) {
        console.warn('Blocked unsupported URL protocol');
        return '';
    }

    return url;
}

/**
 * Sanitizes URL for use in href attributes
 * Combines protocol validation with Veracode-recognized encodeURI()
 * @param url - The URL to sanitize
 * @returns Sanitized and encoded URL safe for href attributes
 */
function sanitizeUrl(url: string): string {
    const validated = validateUrlProtocol(url);
    if (!validated) {
        return '';
    }
    // Use encodeURI() - recognized by Veracode for CWE-80 mitigation
    return encodeURI(validated);
}

const supportedBlockTypes = new Set([
    'paragraph',
    'quote',
    'code',
    'h1',
    'h2',
    'ul',
    'ol'
]);

const blockTypeToBlockName: any = {
    code: 'Code Block',
    h1: 'Large Heading',
    h2: 'Small Heading',
    h3: 'Heading',
    h4: 'Heading',
    h5: 'Heading',
    ol: 'Numbered List',
    paragraph: 'Normal',
    quote: 'Quote',
    ul: 'Bulleted List'
};

function Divider() {
    return <div className="divider" />;
}

function positionEditorElement(editor: any, rect: any) {
    if (rect === null) {
        editor.style.opacity = '0';
        editor.style.top = '-1000px';
        editor.style.left = '-1000px';
    } else {
        editor.style.opacity = '1';
        editor.style.top = `${rect.top + rect.height + window.pageYOffset + 10}px`;
        editor.style.left = `${
            rect.left + window.pageXOffset - editor.offsetWidth / 2 + rect.width / 2
        }px`;
    }
}

function FloatingLinkEditor({editor}: any) {
    const editorRef = useRef(null);
    const inputRef = useRef<any>(null);
    const mouseDownRef = useRef(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [isEditMode, setEditMode] = useState(false);
    const [lastSelection, setLastSelection] = useState<any>(null);

    const updateLinkEditor = useCallback(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
            const node = getSelectedNode(selection);
            const parent = node.getParent();
            if ($isLinkNode(parent)) {
                setLinkUrl(parent.getURL());
            } else if ($isLinkNode(node)) {
                setLinkUrl(node.getURL());
            } else {
                setLinkUrl('');
            }
        }
        const editorElem = editorRef.current;
        const nativeSelection = window.getSelection();
        const activeElement = document.activeElement;

        if (editorElem === null) {
            return;
        }

        const rootElement = editor.getRootElement();
        if (
            selection !== null &&
            !nativeSelection!.isCollapsed &&
            rootElement !== null &&
            rootElement.contains(nativeSelection!.anchorNode)
        ) {
            const domRange = nativeSelection!.getRangeAt(0);
            let rect;
            if (nativeSelection!.anchorNode === rootElement) {
                let inner = rootElement;
                while (inner.firstElementChild !== null) {
                    inner = inner.firstElementChild;
                }
                rect = inner.getBoundingClientRect();
            } else {
                rect = domRange.getBoundingClientRect();
            }

            if (!mouseDownRef.current) {
                positionEditorElement(editorElem, rect);
            }
            setLastSelection(selection);
        } else if (!activeElement || activeElement.className !== 'link-input') {
            positionEditorElement(editorElem, null);
            setLastSelection(null);
            setEditMode(false);
            setLinkUrl('');
        }

        return true;
    }, [editor]);

    useEffect(() => {
        return mergeRegister(
            editor.registerUpdateListener(({editorState}: any) => {
                editorState.read(() => {
                    updateLinkEditor();
                });
            }),

            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                () => {
                    updateLinkEditor();
                    return true;
                },
                LowPriority
            )
        );
    }, [editor, updateLinkEditor]);

    useEffect(() => {
        editor.getEditorState().read(() => {
            updateLinkEditor();
        });
    }, [editor, updateLinkEditor]);

    useEffect(() => {
        if (isEditMode && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isEditMode]);

    return <div className="rich-text-editor">
        <div ref={editorRef} className="link-editor">
            {isEditMode ? (
                <input
                    ref={inputRef}
                    className="link-input"
                    value={linkUrl}
                    onChange={(event) => {
                        setLinkUrl(event.target.value);
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            if (lastSelection !== null) {
                                if (linkUrl !== '') {
                                    // Sanitize URL before applying to prevent XSS
                                    const sanitizedUrl = sanitizeUrl(linkUrl);
                                    if (sanitizedUrl) {
                                        editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizedUrl);
                                    }
                                }
                                setEditMode(false);
                            }
                        } else if (event.key === 'Escape') {
                            event.preventDefault();
                            setEditMode(false);
                        }
                    }}
                />
            ) : (
                <>
                    <div className="link-input">
                        <a href={sanitizeUrl(linkUrl) || '#'} target="_blank" rel="noopener noreferrer">
                            {sanitizeUrl(linkUrl) || 'Invalid URL'}
                        </a>
                        <div
                            className="link-edit"
                            role="button"
                            tabIndex={0}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => {
                                setEditMode(true);
                            }}
                        />
                    </div>
                </>
            )}
        </div>
    </div>;
}

function getSelectedNode(selection: any) {
    const anchor = selection.anchor;
    const focus = selection.focus;
    const anchorNode = selection.anchor.getNode();
    const focusNode = selection.focus.getNode();
    if (anchorNode === focusNode) {
        return anchorNode;
    }
    const isBackward = selection.isBackward();
    if (isBackward) {
        return $isAtNodeEnd(focus) ? anchorNode : focusNode;
    } else {
        return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
    }
}

function BlockOptionsDropdownList({
    editor,
    blockType,
    toolbarRef,
    setShowBlockOptionsDropDown
}: any) {
    const dropDownRef = useRef<any>(null);

    function offset(el: any) {
        let rect = el.getBoundingClientRect(),
            scrollLeft = window.scrollX || document.documentElement.scrollLeft,
            scrollTop = window.scrollY || document.documentElement.scrollTop;
        return {top: rect.top + scrollTop, left: rect.left + scrollLeft};
    }

    useEffect(() => {
        const toolbar = toolbarRef.current;
        const dropDown = dropDownRef.current;

        if (toolbar !== null && dropDown !== null) {
            const {top, left} = offset(toolbar);
            dropDown.style.top = `${top + 40}px`;
            dropDown.style.left = `${left}px`;
        }
    }, [dropDownRef, toolbarRef]);

    useEffect(() => {
        const dropDown = dropDownRef.current;
        const toolbar = toolbarRef.current;

        if (dropDown !== null && toolbar !== null) {
            const handle = (event: any) => {
                const target = event.target;

                if (!dropDown.contains(target) && !toolbar.contains(target)) {
                    setShowBlockOptionsDropDown(false);
                }
            };
            document.addEventListener('click', handle);

            return () => {
                document.removeEventListener('click', handle);
            };
        }
    }, [dropDownRef, setShowBlockOptionsDropDown, toolbarRef]);

    const formatParagraph = () => {
        if (blockType !== 'paragraph') {
            editor.update(() => {
                const selection = $getSelection();

                if ($isRangeSelection(selection)) {
                    $wrapNodes(selection, () => $createParagraphNode());
                }
            });
        }
        setShowBlockOptionsDropDown(false);
    };

    const formatLargeHeading = () => {
        if (blockType !== 'h1') {
            editor.update(() => {
                const selection = $getSelection();

                if ($isRangeSelection(selection)) {
                    $wrapNodes(selection, () => $createHeadingNode('h1'));
                }
            });
        }
        setShowBlockOptionsDropDown(false);
    };

    const formatSmallHeading = () => {
        if (blockType !== 'h2') {
            editor.update(() => {
                const selection = $getSelection();

                if ($isRangeSelection(selection)) {
                    $wrapNodes(selection, () => $createHeadingNode('h2'));
                }
            });
        }
        setShowBlockOptionsDropDown(false);
    };

    const formatBulletList = () => {
        if (blockType !== 'ul') {
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
        } else {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
        }
        setShowBlockOptionsDropDown(false);
    };

    const formatNumberedList = () => {
        if (blockType !== 'ol') {
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
        } else {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
        }
        setShowBlockOptionsDropDown(false);
    };

    const formatQuote = () => {
        if (blockType !== 'quote') {
            editor.update(() => {
                const selection = $getSelection();

                if ($isRangeSelection(selection)) {
                    $wrapNodes(selection, () => $createQuoteNode());
                }
            });
        }
        setShowBlockOptionsDropDown(false);
    };

    return <div className="rich-text-editor ">
        <div className="lexical-dropdown" ref={dropDownRef}>
            <button className="item" onClick={formatParagraph}>
                <span className="icon paragraph" />
                <span className="text">Normal</span>
                {blockType === 'paragraph' && <span className="active" />}
            </button>
            <button className="item" onClick={formatLargeHeading}>
                <span className="icon large-heading" />
                <span className="text">Large Heading</span>
                {blockType === 'h1' && <span className="active" />}
            </button>
            <button className="item" onClick={formatSmallHeading}>
                <span className="icon small-heading" />
                <span className="text">Small Heading</span>
                {blockType === 'h2' && <span className="active" />}
            </button>
            <button className="item" onClick={formatBulletList}>
                <span className="icon bullet-list" />
                <span className="text">Bullet List</span>
                {blockType === 'ul' && <span className="active" />}
            </button>
            <button className="item" onClick={formatNumberedList}>
                <span className="icon numbered-list" />
                <span className="text">Numbered List</span>
                {blockType === 'ol' && <span className="active" />}
            </button>
            <button className="item" onClick={formatQuote}>
                <span className="icon quote" />
                <span className="text">Quote</span>
                {blockType === 'quote' && <span className="active" />}
            </button>
        </div>
    </div>;
}

export function InsertTableDialog({
    activeEditor,
    onClose,
}: {
    activeEditor: LexicalEditor;
    onClose: () => void;
}): JSX.Element {
    const [rows, setRows] = useState('5');
    const [columns, setColumns] = useState('5');

    /**
     * Validates and sanitizes table dimension input
     */
    const validateDimension = (value: string): string => {
        // Only allow digits
        const sanitized = value.replace(/[^0-9]/g, '');
        const num = parseInt(sanitized, 10);

        // Ensure value is between 1 and 100
        if (isNaN(num) || num < 1) {
            return '1';
        }
        if (num > 100) {
            return '100';
        }

        return sanitized;
    };

    const onClick = () => {
        // Validate dimensions before inserting table
        const validatedRows = validateDimension(rows);
        const validatedColumns = validateDimension(columns);

        activeEditor.dispatchCommand(INSERT_TABLE_COMMAND, {
            columns: validatedColumns,
            rows: validatedRows
        });
        onClose();
    };

    return (
        <Modal onClose={onClose} title="Insert Table">
            <div className="rich-text-editor flex flex-col space-y-3">
                <div className="flex flex-col">
                    <label>Number of rows</label>
                    <input
                        onChange={e => setRows(validateDimension(e.target.value))}
                        value={rows}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                    />
                </div>
                <div className="flex flex-col">
                    <label>Number of columns</label>
                    <input
                        placeholder="No of columns"
                        onChange={e => setColumns(validateDimension(e.target.value))}
                        value={columns}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                    />
                </div>
                <div>
                    <Button color="blue" onClick={onClick}>Confirm</Button>
                </div>
            </div>
        </Modal>
    );
}

export default function ToolbarPlugin() {
    const [editor] = useLexicalComposerContext();
    const toolbarRef = useRef(null);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const [blockType, setBlockType] = useState<string>('paragraph');
    const [showBlockOptionsDropDown, setShowBlockOptionsDropDown] = useState(false);
    const [showTableMenu, setShowTableMenu] = useState(false);
    // eslint-disable-next-line
    const [isRTL, setIsRTL] = useState(false);
    const [isLink, setIsLink] = useState(false);
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isStrikethrough, setIsStrikethrough] = useState(false);

    const updateToolbar = useCallback(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
            const anchorNode = selection.anchor.getNode();
            const element =
                anchorNode.getKey() === 'root'
                    ? anchorNode
                    : anchorNode.getTopLevelElementOrThrow();
            const elementKey = element.getKey();
            const elementDOM = editor.getElementByKey(elementKey);
            if (elementDOM !== null) {
                if ($isListNode(element)) {
                    const parentList = $getNearestNodeOfType(anchorNode, ListNode);
                    const type = parentList ? parentList.getTag() : element.getTag();
                    setBlockType(type);
                } else {
                    const type = $isHeadingNode(element)
                        ? element.getTag()
                        : element.getType();
                    setBlockType(type);
                }
            }
            // Update text format
            setIsBold(selection.hasFormat('bold'));
            setIsItalic(selection.hasFormat('italic'));
            setIsUnderline(selection.hasFormat('underline'));
            setIsStrikethrough(selection.hasFormat('strikethrough'));
            setIsRTL($isParentElementRTL(selection));

            // Update links
            const node = getSelectedNode(selection);
            const parent = node.getParent();
            if ($isLinkNode(parent) || $isLinkNode(node)) {
                setIsLink(true);
            } else {
                setIsLink(false);
            }
        }
    }, [editor]);

    useEffect(() => {
        return mergeRegister(
            editor.registerUpdateListener(({editorState}) => {
                editorState.read(() => {
                    updateToolbar();
                });
            }),
            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                // eslint-disable-next-line
                (_payload, newEditor) => {
                    updateToolbar();
                    return false;
                },
                LowPriority
            ),
            editor.registerCommand(
                CAN_UNDO_COMMAND,
                (payload) => {
                    setCanUndo(payload);
                    return false;
                },
                LowPriority
            ),
            editor.registerCommand(
                CAN_REDO_COMMAND,
                (payload) => {
                    setCanRedo(payload);
                    return false;
                },
                LowPriority
            )
        );
    }, [editor, updateToolbar]);

    const insertLink = useCallback(() => {
        if (!isLink) {
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, 'https://');
        } else {
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
        }
    }, [editor, isLink]);

    return (
        <div className="toolbar" ref={toolbarRef}>
            <button
                disabled={!canUndo}
                onClick={() => {
                    //@ts-ignore
                    editor.dispatchCommand(UNDO_COMMAND);
                }}
                className="toolbar-item spaced"
                aria-label="Undo"
            >
                <i className="format undo" />
            </button>
            <button
                disabled={!canRedo}
                onClick={() => {
                    //@ts-ignore
                    editor.dispatchCommand(REDO_COMMAND);
                }}
                className="toolbar-item"
                aria-label="Redo"
            >
                <i className="format redo" />
            </button>
            <Divider />
            {supportedBlockTypes.has(blockType) && (
                <>
                    <button
                        className="toolbar-item block-controls"
                        onClick={() =>
                            setShowBlockOptionsDropDown(!showBlockOptionsDropDown)
                        }
                        aria-label="Formatting Options"
                    >
                        <span className={'icon block-type ' + blockType} />
                        <span className="text">{blockTypeToBlockName[blockType]}</span>
                        <i className="chevron-down" />
                    </button>
                    {showBlockOptionsDropDown &&
                        createPortal(
                            <BlockOptionsDropdownList
                                editor={editor}
                                blockType={blockType}
                                toolbarRef={toolbarRef}
                                setShowBlockOptionsDropDown={setShowBlockOptionsDropDown}
                            />,
                            document.body
                        )}
                    <Divider />
                </>
            )}

            <>
                <button
                    onClick={() => {
                        editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
                    }}
                    className={'toolbar-item spaced ' + (isBold ? 'active' : '')}
                    aria-label="Format Bold"
                >
                    <i className="format bold" />
                </button>
                <button
                    onClick={() => {
                        editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
                    }}
                    className={'toolbar-item spaced ' + (isItalic ? 'active' : '')}
                    aria-label="Format Italics"
                >
                    <i className="format italic" />
                </button>
                <button
                    onClick={() => {
                        editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
                    }}
                    className={'toolbar-item spaced ' + (isUnderline ? 'active' : '')}
                    aria-label="Format Underline"
                >
                    <i className="format underline" />
                </button>
                <button
                    onClick={() => {
                        editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
                    }}
                    className={
                        'toolbar-item spaced ' + (isStrikethrough ? 'active' : '')
                    }
                    aria-label="Format Strikethrough"
                >
                    <i className="format strikethrough" />
                </button>
                <button
                    onClick={insertLink}
                    className={'toolbar-item spaced ' + (isLink ? 'active' : '')}
                    aria-label="Insert Link"
                >
                    <i className="format link" />
                </button>
                {isLink &&
                    createPortal(<FloatingLinkEditor editor={editor} />, document.body)}
                <Divider />
                <button
                    onClick={() => {
                        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
                    }}
                    className="toolbar-item spaced"
                    aria-label="Left Align"
                >
                    <i className="format left-align" />
                </button>
                <button
                    onClick={() => {
                        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
                    }}
                    className="toolbar-item spaced"
                    aria-label="Center Align"
                >
                    <i className="format center-align" />
                </button>
                <button
                    onClick={() => {
                        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
                    }}
                    className="toolbar-item spaced"
                    aria-label="Right Align"
                >
                    <i className="format right-align" />
                </button>
                <button
                    onClick={() => {
                        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify');
                    }}
                    className="toolbar-item"
                    aria-label="Justify Align"
                >
                    <i className="format justify-align" />
                </button>
                <Divider />
                <button
                    onClick={() => {
                        setShowTableMenu(!showTableMenu);
                    }}
                    className="toolbar-item"
                    aria-label="Insert Table"
                >
                    <i className="format table" />
                </button>
            </>
            {showTableMenu && <InsertTableDialog activeEditor={editor} onClose={() => setShowTableMenu(false)} />}
        </div>
    );
}
